import React from "react";
import styled from "styled-components";
import {
  FileText,
  Users,
  Shield,
  AlertTriangle,
  Globe,
  Mail,
  Scale,
} from "lucide-react";

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
  line-height: 1.7;
  color: var(--text-color);
  background: var(--background-color);
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-color);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const LastUpdated = styled.p`
  color: var(--text-secondary-color);
  font-size: 0.9rem;
  margin-bottom: 2rem;
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SectionContent = styled.div`
  color: var(--text-secondary-color);
  margin-bottom: 1.5rem;

  p {
    margin-bottom: 1rem;
  }

  ul {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
  }

  strong {
    color: var(--text-color);
    font-weight: 600;
  }

  ol {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }
`;

const WarningBox = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  border-left: 4px solid #ef4444;
`;

const ContactInfo = styled.div`
  background: var(--surface-color);
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 2rem;
  text-align: center;
`;

const ContactTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const ContactLink = styled.a`
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const TermsOfServicePage: React.FC = () => {
  return (
    <PageContainer>
      <Header>
        <Title>
          <FileText size={32} />
          Terms of Service
        </Title>
        <LastUpdated>Last updated: September 2025</LastUpdated>
      </Header>

      <Section>
        <SectionTitle>
          <Globe size={24} />
          Acceptance of Terms
        </SectionTitle>
        <SectionContent>
          <p>
            By accessing and using NewME.social ("the Platform"), you accept and
            agree to be bound by the terms and provision of this agreement. If
            you do not agree to abide by the above, please do not use this
            service.
          </p>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <Users size={24} />
          Description of Service
        </SectionTitle>
        <SectionContent>
          <p>
            NewME.social is a collaborative platform that allows users to
            create, share, and discuss maps and state designs for the Middle
            East region. Our platform facilitates:
          </p>
          <ul>
            <li>Interactive map creation and editing tools</li>
            <li>Public sharing and community discussion of maps</li>
            <li>User profiles and social features</li>
            <li>Template and overlay management</li>
            <li>Community moderation and approval systems</li>
          </ul>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <Users size={24} />
          User Accounts
        </SectionTitle>
        <SectionContent>
          <h4>Account Creation:</h4>
          <ul>
            <li>
              You are responsible for maintaining the confidentiality of your
              account
            </li>
            <li>You must be at least 13 years old to create an account</li>
          </ul>

          <h4>Account Security:</h4>
          <ul>
            <li>
              You are responsible for all activities that occur under your
              account
            </li>
            <li>
              Notify us immediately of any unauthorized use of your account
            </li>
            <li>
              We reserve the right to suspend or terminate accounts that violate
              these terms
            </li>
          </ul>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <Shield size={24} />
          Acceptable Use
        </SectionTitle>
        <SectionContent>
          <p>
            You agree to use the Platform only for lawful purposes and in
            accordance with these Terms. You agree NOT to:
          </p>

          <WarningBox>
            <strong>Prohibited Activities:</strong>
            <ul>
              <li>
                Create content that promotes violence, hatred, or discrimination
              </li>
              <li>Share false, misleading, or defamatory information</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights of others</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>
                Use the Platform for commercial purposes without permission
              </li>
              <li>Spam, harass, or abuse other users</li>
              <li>
                Create content that violates human rights or promotes oppression
              </li>
            </ul>
          </WarningBox>

          <p>
            We reserve the right to remove content and suspend accounts that
            violate these guidelines.
          </p>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <FileText size={24} />
          User Content
        </SectionTitle>
        <SectionContent>
          <h4>Your Content:</h4>
          <ul>
            <li>You retain ownership of content you create on the Platform</li>
            <li>
              You grant us a license to display, distribute, and modify your
              content as necessary to operate the Platform
            </li>
            <li>
              You are responsible for ensuring you have the right to share any
              content you post
            </li>
            <li>You may delete your content at any time</li>
          </ul>

          <h4>Content Moderation:</h4>
          <ul>
            <li>
              We may review, edit, or remove content that violates these terms
            </li>
            <li>We use both automated systems and human moderators</li>
            <li>Decisions about content removal are at our sole discretion</li>
            <li>
              You may appeal content removal decisions through our support
              system
            </li>
          </ul>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <AlertTriangle size={24} />
          Disclaimers and Limitations
        </SectionTitle>
        <SectionContent>
          <h4>Service Availability:</h4>
          <ul>
            <li>
              The Platform is provided "as is" without warranties of any kind
            </li>
            <li>We do not guarantee uninterrupted or error-free service</li>
            <li>We may modify or discontinue features at any time</li>
          </ul>

          <h4>Limitation of Liability:</h4>
          <ul>
            <li>
              We are not liable for any indirect, incidental, or consequential
              damages
            </li>
            <li>
              Our total liability is limited to the amount you paid us in the
              past 12 months
            </li>
            <li>We are not responsible for content created by other users</li>
          </ul>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <Shield size={24} />
          Privacy
        </SectionTitle>
        <SectionContent>
          <p>
            Your privacy is important to us. Please review our{" "}
            <ContactLink href="/privacy">Privacy Policy</ContactLink> to
            understand how we collect, use, and protect your information.
          </p>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <Users size={24} />
          Community Guidelines
        </SectionTitle>
        <SectionContent>
          <p>To maintain a respectful and productive community, please:</p>
          <ul>
            <li>Be respectful and constructive in discussions</li>
            <li>
              Focus on the topic of Middle Eastern state design and
              self-determination
            </li>
            <li>Provide constructive feedback on maps and proposals</li>
            <li>Respect different viewpoints and cultural perspectives</li>
            <li>Report inappropriate content or behavior</li>
          </ul>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <FileText size={24} />
          Termination
        </SectionTitle>
        <SectionContent>
          <p>
            We may terminate or suspend your account and access to the Platform
            immediately, without prior notice, for any reason, including if you
            breach these Terms.
          </p>

          <p>You may terminate your account at any time by contacting us.</p>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <Globe size={24} />
          Changes to Terms
        </SectionTitle>
        <SectionContent>
          <p>
            We reserve the right to modify these Terms at any time. We will
            notify users of significant changes by posting the updated Terms on
            the Platform and updating the "Last updated" date.
          </p>

          <p>
            Your continued use of the Platform after changes constitutes
            acceptance of the updated Terms.
          </p>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          <Scale size={24} />
          Governing Law
        </SectionTitle>
        <SectionContent>
          <p>
            These Terms are governed by and construed in accordance with
            applicable laws. Any disputes arising from these Terms or your use
            of the Platform will be resolved through appropriate legal channels.
          </p>
        </SectionContent>
      </Section>

      <ContactInfo>
        <ContactTitle>
          <Mail size={20} />
          Contact Us
        </ContactTitle>
        <p>
          If you have any questions about these Terms of Service, please{" "}
          <ContactLink href="/contact">contact us</ContactLink>.
        </p>
      </ContactInfo>
    </PageContainer>
  );
};

export default TermsOfServicePage;
